/* LivePresenceModal — full-screen educational modal for the "Chi c'è online"
   map feature. Mirrors the RadarToggle pattern: big hero illustration, toggle
   visibility, peer list, and a link to Match.

   Toggle explanation:
   - Privacy-first: user must opt-in; disabling wipes the last known position.
   - Radius: 25km (spec P1 social loop v18.74).
   - Peers refresh every 45s; heartbeat every 60s.
   - Salutare un utente → arriva nel loro Match inbox (source='presence').
*/
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, X, ShieldCheck, Users2, Sparkles, Info, Hand } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { hapticTap, hapticSuccess } from "@/lib/haptics";
import { orioIllustration, orioIllustrationFallback } from "@/lib/illustrations";

const HEARTBEAT_MS = 60_000;
const PEERS_REFRESH_MS = 45_000;
const RADIUS_KM = 25;

function coerceLoc(pos) {
  if (!pos) return null;
  if (Array.isArray(pos)) return { lat: pos[0], lng: pos[1] };
  if (typeof pos === "object" && pos.lat != null && pos.lng != null) return { lat: pos.lat, lng: pos.lng };
  return null;
}

/**
 * Trigger + Modal component. Renders nothing when both `open=false` and no
 * peers — parent component controls open state. This mirrors RadarToggle.
 */
export default function LivePresenceModal({ open, onClose, me, onPeersChange, onVisibleChange }) {
  const [visible, setVisible] = useState(false);
  const [peers, setPeers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [greeted, setGreeted] = useState({}); // { userId: true }
  const hbRef = useRef(null);
  const pollRef = useRef(null);
  const loc = coerceLoc(me);

  // Read initial visibility state once
  useEffect(() => {
    let cancelled = false;
    api.get("/me/visibility").then((r) => {
      if (!cancelled) {
        const v = !!r.data?.visible;
        setVisible(v);
        onVisibleChange?.(v);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const loadPeers = React.useCallback(async () => {
    if (!visible || !loc) return;
    try {
      const r = await api.get("/missioni/visible-users", {
        params: { lat: loc.lat, lng: loc.lng, radius_km: RADIUS_KM },
      });
      const items = r.data?.items || [];
      setPeers(items);
      onPeersChange?.(items);
    } catch {/* ignore */}
  }, [visible, loc, onPeersChange]);

  // Heartbeat + peers poller (only when visible)
  useEffect(() => {
    if (hbRef.current) { clearInterval(hbRef.current); hbRef.current = null; }
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (!visible || !loc) {
      setPeers([]);
      onPeersChange?.([]);
      return;
    }
    const ping = () => api.post("/me/heartbeat", { lat: loc.lat, lng: loc.lng }).catch(() => {});
    ping();
    hbRef.current = setInterval(ping, HEARTBEAT_MS);
    loadPeers();
    pollRef.current = setInterval(loadPeers, PEERS_REFRESH_MS);
    return () => {
      if (hbRef.current) clearInterval(hbRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [visible, loc, loadPeers, onPeersChange]);

  const toggleVisibility = async () => {
    if (!loc) {
      toast.error("Attiva prima la posizione dal tasto GPS in mappa");
      return;
    }
    hapticTap();
    setLoading(true);
    const next = !visible;
    try {
      await api.put("/me/visibility", { visible: next, lat: loc.lat, lng: loc.lng });
      setVisible(next);
      onVisibleChange?.(next);
      if (next) {
        hapticSuccess();
        toast.success("Sei visibile su ORIO · raggio 25km", { icon: "🟢" });
      } else {
        toast.success("Modalità invisibile · nessuno ti vede");
        setPeers([]);
        onPeersChange?.([]);
      }
    } catch {
      toast.error("Impossibile aggiornare la visibilità");
    } finally {
      setLoading(false);
    }
  };

  const greet = async (peer) => {
    if (greeted[peer.id]) return;
    hapticTap();
    setGreeted((g) => ({ ...g, [peer.id]: true }));
    try {
      await api.post("/presence/greet", { to_user_id: peer.id });
      toast.success(`Saluto inviato a ${peer.name || "utente"}`, { icon: "👋" });
    } catch (e) {
      setGreeted((g) => { const n = { ...g }; delete n[peer.id]; return n; });
      toast.error("Non riesco a inviare il saluto");
    }
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[1490]"
            style={{ background: "rgba(20,20,22,0.42)", backdropFilter: "blur(4px)" }}
          />
          {/* Sheet */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ type: "spring", damping: 26, stiffness: 240 }}
            className="fixed inset-x-0 top-0 z-[1500] flex flex-col"
            style={{
              background: "#FBF7F1",
              height: "100dvh",
              paddingBottom: "max(env(safe-area-inset-bottom, 0px) + 96px, 96px)",
            }}
            data-testid="presence-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-4 pb-2 shrink-0"
                 style={{ background: "linear-gradient(180deg, #FBF7F1 0%, rgba(251,247,241,0.9) 100%)" }}>
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#9A9A9A] font-semibold inline-flex items-center gap-1.5">
                  <Users2 className="h-3 w-3" /> Presenza dal vivo
                </div>
                <div className="font-display text-xl leading-tight mt-0.5">
                  Chi c&apos;è online<br />
                  <span className="orio-grad-text">nei tuoi 25 km</span>
                </div>
              </div>
              <button onClick={onClose} data-testid="presence-modal-close"
                      className="h-10 w-10 rounded-full bg-white border border-[#F0EBE3] flex items-center justify-center"
                      style={{ boxShadow: "0 4px 12px -4px rgba(0,0,0,0.15)" }}>
                <X className="h-4 w-4 text-[#1A1A1A]" />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-4 pt-2" data-testid="presence-modal-scroll">
              {/* Hero illustration */}
              <div className="rounded-3xl overflow-hidden border relative"
                   style={{ borderColor: "#F0EBE3", aspectRatio: "5 / 3", background: "#F4EFE6" }}>
                <img
                  src={orioIllustration("feat-presence")}
                  onError={(e) => { e.currentTarget.src = orioIllustrationFallback("feat-presence"); }}
                  alt="Chi c'è online"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              {/* Descrittore SOTTO l'immagine per massima leggibilità */}
              <div className="mt-3 rounded-2xl p-3 border" style={{ background: "white", borderColor: "#F0EBE3" }}>
                <div className="text-[12.5px] text-[#4A4A4A] leading-relaxed">
                  <strong className="text-[#1A1A1A]">Presenza live</strong>: attivando la visibilità, gli utenti ORIO nei tuoi 25 km possono vederti sulla mappa.
                  Tu vedi loro, loro vedono te — e potete <strong className="text-[#1A1A1A]">salutarvi</strong>: il saluto arriva direttamente nel loro Match.
                </div>
              </div>

              {/* Big visibility toggle */}
              <button
                data-testid="presence-visibility-toggle"
                onClick={toggleVisibility}
                disabled={loading}
                className="w-full mt-4 rounded-2xl p-4 flex items-center gap-3 border transition"
                style={{
                  background: visible
                    ? "linear-gradient(135deg, #34D399 0%, #2DBA60 100%)"
                    : "white",
                  borderColor: visible ? "#2DBA60" : "#F0EBE3",
                  color: visible ? "white" : "#1A1A1A",
                  boxShadow: visible
                    ? "0 12px 26px -10px rgba(45,186,96,0.55)"
                    : "0 4px 12px -6px rgba(0,0,0,0.10)",
                }}
              >
                <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0"
                     style={{ background: visible ? "rgba(255,255,255,0.25)" : "#E8F4EA", color: visible ? "white" : "#2DBA60" }}>
                  {visible ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-[14.5px] font-semibold leading-tight">
                    {visible ? "Sei visibile" : "Rendimi visibile"}
                  </div>
                  <div className="text-[11.5px] opacity-90 mt-0.5">
                    {visible
                      ? `Raggio 25 km · ${peers.length} online adesso`
                      : "Solo chi attiva la visibilità appare in mappa"}
                  </div>
                </div>
                <div className="h-6 w-11 rounded-full transition relative shrink-0"
                     style={{ background: visible ? "rgba(255,255,255,0.4)" : "#E8E2D6" }}>
                  <div className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all"
                       style={{ left: visible ? 22 : 2 }} />
                </div>
              </button>

              {/* Peer list (only when visible) */}
              {visible && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#9A9A9A] mb-2 px-1">
                    {peers.length > 0 ? `Nei tuoi 25 km · ${peers.length} online` : "Nessuno online nei tuoi 25 km"}
                  </div>
                  {peers.length === 0 && (
                    <div className="rounded-2xl border border-dashed p-4 text-center text-[12px] text-[#7A7A7A]"
                         style={{ borderColor: "#E5DECF", background: "white" }}>
                      Torna tra qualche minuto: quando un altro utente attiverà la visibilità apparirà qui.
                    </div>
                  )}
                  <div className="space-y-1.5">
                    {peers.map((p) => (
                      <PeerRow key={p.id} peer={p} greeted={!!greeted[p.id]} onGreet={() => greet(p)} />
                    ))}
                  </div>
                </div>
              )}

              {/* Match integration teaser */}
              <div className="mt-5 rounded-2xl p-4 border" style={{ background: "white", borderColor: "#F0EBE3" }}>
                <div className="flex items-start gap-2.5">
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                       style={{ background: "#FFF1DF", color: "#FF7A1A" }}>
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-[12.5px] font-semibold text-[#1A1A1A]">Integrato con Match</div>
                    <div className="text-[11px] text-[#7A7A7A] leading-relaxed mt-1">
                      Ogni saluto 👋 arriva nel Match dell&apos;altro come like anonimo. Se ricambia, si apre una chat 1 v 1.
                      Il modo più veloce per fare amicizia in passeggiata.
                    </div>
                  </div>
                </div>
              </div>

              {/* Privacy */}
              <div className="mt-3 mb-4 rounded-2xl p-4 border" style={{ background: "white", borderColor: "#F0EBE3" }}>
                <div className="flex items-start gap-2.5">
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                       style={{ background: "#E8F4EA", color: "#2DBA60" }}>
                    <ShieldCheck className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-[12px] font-semibold text-[#1A1A1A]">Privacy prima di tutto</div>
                    <div className="text-[11px] text-[#7A7A7A] leading-relaxed mt-1">
                      Attivi tu quando vuoi. Se disattivi la visibilità, l&apos;ultima posizione viene cancellata subito.
                      Chi è offline da più di 5 minuti scompare automaticamente dalla mappa.
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-1 mb-4 rounded-2xl p-3 border-2 border-dashed" style={{ borderColor: "#F0EBE3" }}>
                <div className="flex items-start gap-2 text-[11px] text-[#7A7A7A] leading-relaxed">
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "#B0A084" }} />
                  <span>Il raggio 25 km è fisso. Riceverai comunque tutti gli alert Radar entro 40 km, anche se sei invisibile.</span>
                </div>
              </div>
            </div>

            {/* Sticky bottom CTA */}
            <div className="absolute inset-x-0 z-[1510] px-4 pt-2 pb-3 flex gap-2"
                 style={{
                   bottom: "max(env(safe-area-inset-bottom, 0px) + 20px, 20px)",
                   background: "linear-gradient(180deg, rgba(251,247,241,0) 0%, rgba(251,247,241,0.92) 40%, #FBF7F1 100%)",
                 }}>
              <button onClick={onClose} data-testid="presence-apply-close"
                      className="flex-1 h-12 rounded-full text-[13px] font-semibold text-white inline-flex items-center justify-center gap-2"
                      style={{ background: visible
                        ? "linear-gradient(135deg, #34D399 0%, #2DBA60 100%)"
                        : "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)",
                        boxShadow: "0 18px 30px -12px rgba(45,186,96,0.5)" }}>
                {visible ? `Applica · ${peers.length} online` : "Chiudi"}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}

function PeerRow({ peer, greeted, onGreet }) {
  const initials = (peer.name || "?").split(" ").slice(0, 2).map((w) => w[0] || "").join("").toUpperCase();
  return (
    <div
      data-testid={`presence-peer-${peer.id}`}
      className="flex items-center gap-3 p-2.5 rounded-2xl bg-white border border-[#F0EBE3]"
    >
      <div className="relative shrink-0">
        <div className="h-11 w-11 rounded-full overflow-hidden border-2 border-white flex items-center justify-center text-[11px] font-semibold text-white"
             style={{
               background: peer.photo_url ? "transparent" : "linear-gradient(135deg,#34D399,#2DBA60)",
               boxShadow: "0 3px 8px -3px rgba(45,186,96,0.4)",
             }}>
          {peer.photo_url
            ? <img src={peer.photo_url} alt={peer.name || ""} className="w-full h-full object-cover" />
            : <span>{initials}</span>}
        </div>
        <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-[#2DBA60] border-2 border-white" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-[#1A1A1A] leading-tight truncate">
          {peer.name || "Utente ORIO"}
        </div>
        {peer.dog?.name ? (
          <div className="text-[11px] text-[#7A7A7A] leading-tight truncate">
            🐾 {peer.dog.name}{peer.dog.breed ? ` · ${peer.dog.breed}` : ""}
          </div>
        ) : (
          <div className="text-[11px] text-[#B0A084] leading-tight">Nella tua zona</div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <div className="text-[11px] font-semibold text-[#2DBA60]">{peer.distance_km} km</div>
        <button
          data-testid={`presence-greet-${peer.id}`}
          disabled={greeted}
          onClick={onGreet}
          className="h-9 px-3 rounded-full text-[12px] font-semibold inline-flex items-center gap-1.5 transition"
          style={{
            background: greeted ? "#E8F4EA" : "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
            color: greeted ? "#2DBA60" : "white",
            boxShadow: greeted ? "none" : "0 6px 16px -6px rgba(255,122,26,0.55)",
          }}
        >
          <Hand className="h-3.5 w-3.5" />
          {greeted ? "Inviato" : "Saluta"}
        </button>
      </div>
    </div>
  );
}
